import { ref, computed, inject, onMounted } from "vue"
import { Services } from "@services"
import type { CompanyUser } from "@core/services/user.service"

// =====================
// Types
// =====================

/**
 * Suggestion d'utilisateur pour l'autocomplétion
 */
export type UserSuggestion = {
    /**
     * Valeur unique (utilisée pour la sélection)
     */
    value: string
    /**
     * Nom affiché dans la liste
     */
    name: string
    /**
     * Données complètes de l'utilisateur
     */
    user: CompanyUser
}

/**
 * Options du composable
 */
export type UseUserSearchOptions = {
    /**
     * Types d'utilisateurs à rechercher
     * @default ["employe", "administrateur", "employe_read_only", "owner"]
     */
    types?: string[]
    /**
     * Charger les suggestions au montage
     * @default true
     */
    fetchOnMount?: boolean
}

// =====================
// Composable
// =====================

/**
 * Composable pour la recherche d'utilisateurs avec autocomplétion
 * @param options - Options de configuration
 * @returns Suggestions, état de recherche et callback de sélection
 */
export function useUserSearch(options: UseUserSearchOptions = {}) {
    const {
        types = ["employe", "administrateur", "employe_read_only", "owner"],
        fetchOnMount = true,
    } = options

    const { $getCompanyUsers, $getUserByUsername } =
        inject<Services>("services")

    // =====================
    // État
    // =====================

    /**
     * Liste des suggestions d'utilisateurs
     */
    const suggestions = ref<UserSuggestion[]>([])

    /**
     * Indique si la recherche est en cours
     */
    const isSearching = ref(false)

    /**
     * Erreur de recherche
     */
    const error = ref<string | null>(null)

    // =====================
    // Computed
    // =====================

    /**
     * Indique si des suggestions sont disponibles
     */
    const hasSuggestions = computed(() => suggestions.value.length > 0)

    // =====================
    // Actions
    // =====================

    /**
     * Charge les utilisateurs de l'entreprise
     */
    async function fetchCompanyUsers() {
        if (!$getCompanyUsers) {
            console.warn("Service $getCompanyUsers non disponible")
            return
        }

        try {
            isSearching.value = true
            error.value = null

            const users = await $getCompanyUsers(types)

            suggestions.value = users.map((user) => ({
                value: user._id,
                name: user.username,
                user,
            }))
        } catch (err) {
            console.error("Erreur lors de la recherche d'utilisateurs:", err)
            error.value = "Erreur lors de la recherche d'utilisateurs"
            suggestions.value = []
        } finally {
            isSearching.value = false
        }
    }

    /**
     * Callback appelé lors de la sélection d'un utilisateur dans l'autocomplétion
     * @param selectedValue - Valeur sélectionnée (id ou email)
     * @param onFound - Callback appelé si l'utilisateur est trouvé
     * @param onNotFound - Callback appelé si l'utilisateur n'est pas trouvé
     */
    function onSelectUser(
        selectedValue: string,
        onFound?: (suggestion: UserSuggestion) => void,
        onNotFound?: () => void
    ) {
        // Réinitialiser si appelé sans valeur
        if (onNotFound) {
            onNotFound()
        }

        // Rechercher dans les suggestions
        const found = suggestions.value.find((s) => s.value === selectedValue)

        if (found && onFound) {
            onFound(found)
        }
    }

    /**
     * Recherche un utilisateur par son email via l'API
     * @param username - Email de l'utilisateur
     * @returns L'utilisateur trouvé ou null
     */
    async function searchUserByEmail(username: string) {
        if (!$getUserByUsername) {
            console.warn("Service $getUserByUsername non disponible")
            return null
        }

        try {
            isSearching.value = true
            error.value = null

            const user = await $getUserByUsername(username)
            return user
        } catch (err) {
            console.error("Erreur lors de la recherche d'utilisateur:", err)
            error.value = "Erreur lors de la recherche d'utilisateur"
            return null
        } finally {
            isSearching.value = false
        }
    }

    /**
     * Réinitialise les suggestions
     */
    function clearSuggestions() {
        suggestions.value = []
        error.value = null
    }

    // =====================
    // Lifecycle
    // =====================

    if (fetchOnMount) {
        onMounted(() => {
            fetchCompanyUsers()
        })
    }

    return {
        // État
        suggestions,
        isSearching,
        error,

        // Computed
        hasSuggestions,

        // Actions
        fetchCompanyUsers,
        onSelectUser,
        searchUserByEmail,
        clearSuggestions,
    }
}
